import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { useUser } from "../../context/UserContext";
import { selectLoginIdentifier } from "../../services/login";
import { setCookie } from "../../utils/common";
import { landingFor } from "../../constants/personaRoutes";
import LoginPage from "./LoginPage";

/**
 * /switch?linkId=N — cross-domain persona handoff.
 *
 * Each persona family lives on its own domain, so a login that holds several sees the
 * ones it cannot use HERE greyed out with a link to where they live. That link points
 * at this route on the target domain, carrying the identifier link id.
 *
 * With a session already present (cookies are scoped to .buildvizer.com in production,
 * so the one from the originating domain is readable here) this exchanges the link for
 * a token bound to that persona and drops the user on the right landing page — no
 * second sign-in, no picker.
 *
 * Without a session — cookies still host-only, a cold browser, or a link someone
 * forwarded — it falls through to the normal login screen with a note explaining what
 * they were trying to reach. Deliberately NOT an error: an expired or absent session on
 * a deep link is ordinary, not exceptional.
 */
function PersonaHandoff() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { userInfo, setUserInfo } = useUser();
  const [failed, setFailed] = useState(false);
  // React 18 StrictMode double-invokes effects in development; the exchange is
  // single-use on the server, so guard it.
  const attempted = useRef(false);

  const linkId = Number(params.get("linkId"));
  const hasSession = !!userInfo?.accessToken;

  useEffect(() => {
    if (!linkId || !hasSession || attempted.current) return;
    attempted.current = true;

    let cancelled = false;
    selectLoginIdentifier({ linkId, remember: true })
      .then((fresh) => {
        if (cancelled) return;
        if (!fresh?.accessToken || !fresh?.persona) {
          setFailed(true);
          return;
        }
        setCookie("_USER_AUTH_", fresh);
        setCookie("accessToken", fresh.accessToken, { path: "/", expires: 1 });
        setUserInfo(fresh);
        navigate(landingFor(fresh.persona), { replace: true });
      })
      .catch((err) => {
        // A 403 here is legitimate and expected: the backend re-checks that THIS app
        // may serve that persona, so a hand-crafted or stale link lands here.
        console.warn("Persona handoff failed:", err?.detail || err?.message || err);
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [linkId, hasSession, navigate, setUserInfo]);

  // No session, no usable link, or the exchange was refused — sign in normally.
  if (!linkId || !hasSession || failed) {
    return <LoginPage handoffNotice={linkId ? "Sign in to continue to this account." : null} />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50">
      <div className="flex flex-col items-center gap-3 rounded-2xl bg-white px-10 py-8 shadow-sm">
        <Loader2 className="animate-spin text-flamingo-500" size={28} />
        <p className="text-sm text-slate-600">Switching account…</p>
      </div>
    </div>
  );
}

export default PersonaHandoff;
